/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {SystemFindNearestIcon} from '@ion/dls/src/icons/current/SystemFindNearestIcon';
import {IconSize} from '@ion/dls/src/types/types';
import {TertiaryIconButton, TertiaryIconButtonProps} from './TertiaryIconButton';

export interface FindNearbyIconButtonProps extends TertiaryIconButtonProps {}

/**
 * The FindNearbyIconButton is a TertiaryIconButton that displays a
 * target/radar icon with "Find Nearby" text. Icon placement is specified
 * as it would be for the TertiaryIconButton. Other TertiaryIconButton props
 * may also be specified (except "icon"/"label" props, which are fixed).
 *
 * There is no reusable 1:1 click handler for this action inside the DLS
 * package - the legacy `PanelComments` (`@ion/dls/src/components/sections/
 * PanelComments`) accepts its `findNearbyClick` as a caller-supplied prop,
 * and the concrete handler (`findNearest`) is wired at the route level in
 * `AssetDetailsAccordion.tsx`, outside this ticket's scope - so this button
 * simply forwards whatever `onClick` its caller supplies.
 *
 * ```typescript
 *
 *   import React from 'react';
 *   import {FindNearbyIconButton} from '@ion/dls/src/components/buttons/';
 *   ...
 *
 *   function MyComponent {
 *     return (
 *       <React.Fragment>
 *         <FindNearbyIconButton onClick={onFindNearby} iconDirection='left' />
 *         <br />
 *         <FindNearbyIconButton onClick={onFindNearby} />
 *       </React.Fragment>
 *     );
 *   }
 *
 * ```
 *
 */
export const FindNearbyIconButton = (props: FindNearbyIconButtonProps) => {
  return (
    <TertiaryIconButton {...props}
      icon={<SystemFindNearestIcon size={IconSize.medium} />}
      label={translate('tracking:buttons.findNearby')} />
  );
};

export default React.memo(FindNearbyIconButton);
